import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Receipt,
  Users,
  CurrencyCircleDollar,
  CalendarCheck,
  WarningCircle,
  TrendUp,
  Star,
} from '@phosphor-icons/react';
import { bizGet } from '@/shared/api/bizClient';

interface ReportSummary {
  ordersThisMonth: number;
  revenueThisMonthMinor: number;
  customersTotal: number;
  customersNewThisMonth: number;
  bookingsThisMonth: number;
  bookingsPaidThisMonth: number;
  lowStockItemCount: number;
  topServiceNames: string[];
}

function formatCurrency(minor: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(minor / 100);
}

function getMonthLabel(): string {
  return new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
}

export function ReportsPage(): JSX.Element {
  const { t } = useTranslation('layout');
  const { data, isLoading, isError } = useQuery<ReportSummary>({
    queryKey: ['biz-reports-summary'],
    queryFn: () => bizGet<ReportSummary>('/biz/reports/summary'),
  });

  return (
    <div className="flex flex-col gap-6 p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('nav.reports')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{getMonthLabel()} — Aylık Özet</p>
      </div>

      {isError && (
        <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded-xl text-sm">
          <WarningCircle size={16} />
          Rapor verisi yüklenemedi. Lütfen sayfayı yenileyin.
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard
          icon={<CurrencyCircleDollar size={24} className="text-green-600" />}
          label="Bu Ay Ciro"
          value={isLoading ? '...' : formatCurrency(data?.revenueThisMonthMinor ?? 0)}
          sub="Ödenen siparişler"
          color="bg-green-50 border-green-200"
        />
        <KpiCard
          icon={<Receipt size={24} className="text-blue-600" />}
          label="Bu Ay Sipariş"
          value={isLoading ? '...' : String(data?.ordersThisMonth ?? 0)}
          sub="Tamamlanan"
          color="bg-blue-50 border-blue-200"
        />
        <KpiCard
          icon={<CalendarCheck size={24} className="text-violet-600" />}
          label="Bu Ay Randevu"
          value={isLoading ? '...' : String(data?.bookingsThisMonth ?? 0)}
          sub={`${data?.bookingsPaidThisMonth ?? 0} ödendi`}
          color="bg-violet-50 border-violet-200"
        />
        <KpiCard
          icon={<Users size={24} className="text-amber-600" />}
          label="Toplam Müşteri"
          value={isLoading ? '...' : String(data?.customersTotal ?? 0)}
          sub={`+${data?.customersNewThisMonth ?? 0} bu ay yeni`}
          color="bg-amber-50 border-amber-200"
        />
        {(data?.lowStockItemCount ?? 0) > 0 && (
          <KpiCard
            icon={<WarningCircle size={24} className="text-orange-600" />}
            label="Düşük Stok"
            value={String(data?.lowStockItemCount)}
            sub="Yenileme gerekebilir"
            color="bg-orange-50 border-orange-300"
          />
        )}
      </div>

      {/* Top Services */}
      {(data?.topServiceNames?.length ?? 0) > 0 && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendUp size={20} className="text-primary" />
            <h2 className="font-semibold text-foreground">Bu Ay En Çok Satılan Hizmetler</h2>
          </div>
          <div className="flex flex-col gap-2">
            {data!.topServiceNames.map((name, i) => (
              <div key={name} className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{name}</span>
                  {i === 0 && <Star size={14} className="text-amber-500 fill-amber-500" weight="fill" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment rate */}
      {data && data.bookingsThisMonth > 0 && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <CalendarCheck size={20} className="text-primary" />
            <h2 className="font-semibold text-foreground">Randevu Ödeme Oranı</h2>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-3xl font-bold text-foreground">
              {Math.round((data.bookingsPaidThisMonth / data.bookingsThisMonth) * 100)}%
            </span>
            <span className="text-sm text-muted-foreground mb-1">
              ({data.bookingsPaidThisMonth}/{data.bookingsThisMonth} randevu ödendi)
            </span>
          </div>
          <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{
                width: `${Math.round((data.bookingsPaidThisMonth / data.bookingsThisMonth) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && data?.ordersThisMonth === 0 && data?.bookingsThisMonth === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <TrendUp size={48} className="text-muted-foreground mb-4" />
          <p className="font-medium text-muted-foreground">Bu ay için henüz veri yok</p>
          <p className="text-sm text-muted-foreground mt-1">
            Randevu ve sipariş eklendikçe raporlar burada görünecek.
          </p>
        </div>
      )}
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: JSX.Element;
  label: string;
  value: string;
  sub?: string;
  color?: string;
}): JSX.Element {
  return (
    <div className={`rounded-2xl border p-4 ${color ?? 'bg-card border-border'}`}>
      <div className="flex items-center gap-2 mb-2">{icon}</div>
      <p className="text-2xl font-bold text-foreground leading-none mb-1">{value}</p>
      <p className="text-xs font-medium text-foreground/70">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
